/* eslint-disable no-use-before-define */
import { v4 as uuid } from 'uuid';
import { generatePath } from 'react-router';
import { RxSocketUrls } from '@Constants/CommonConstants';
import { sanitizeEscape } from '@Services/HtmlSanitizeService';
import HistoryResource from '@Services/HistoryResource';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import Breadcrumb from '@Services/Breadcrumb';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import store from '@Store';
import { generateResultsPath, generateHeatmapPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import UserDatabases from '@Services/UserDatabases';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import HistoryHelper from '@Services/HistoryHelper';
import SearchStorage from '@Services/SearchStorage';
import SocketBroker from '@Services/SocketBroker';
import RxSocket from '@Services/RxSocket';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { SearchTypes, typeOfHistorySaveDialog, typeOfHistoryConfirmationDialog, HANDLE_VIEW_SAVE_MODAL } from '@Pages/History/Constants';
import { paths } from '@AppRoutes/constants';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';

import get from 'lodash/get';
import each from 'lodash/each';
import assignIn from 'lodash/assignIn';
import flow from 'lodash/fp/flow';
import groupBy from 'lodash/fp/groupBy';
import map from 'lodash/fp/map';
import toArray from 'lodash/fp/toArray';
import compact from 'lodash/fp/compact';
import sortBy from 'lodash/fp/sortBy';

const DEFAULT_QUICK_SEARCH_OPTIONS = [{ name: 'searchBy', value: 'exact' }];
const HISTORY_SEARCH_REFRESH_TIMEOUT_MS = 30000;

let _searchByQuery = null;
export const registerSearchByQuery = (fn) => {
  _searchByQuery = fn;
};

// Pinpoints the exact History path to return to after modal login, since visitedStates
// tracking has stale-read timing issues when the pathname just changed.
const HISTORY_LOGIN_REDIRECT_PATH_STORAGE_KEY = 'historyLoginRedirectPath';
export const getHistoryTypes = () => HistoryHelper.getHistoryTypes();

export function formatNewHistory(...args) {
  return HistoryHelper.formatNewHistory(...args);
}

export function storeRecentZeroSearch(result, historyType, factItem) {
  const currentUser = AuthUtils.getUser();
  const recentSearches = get(currentUser, 'history.recentSearches', []);
  const creationTime = Date.now();

  const zeroResult = {
    context: result.payload.context,
    databaseId: result.payload.databaseId,
    queryId: result.payload.queryId,
    resultName: result.payload.resultName,
    searchId: result.payload.searchId,
    hitSetKey: result.payload.hitSetKey,
    isFromAdvancedSearch: true,
    totalResult: result.payload.resultSize,
    creationTime,
    queryFactItem: factItem,
    historyType,
    resultSize: result.payload.resultSize,
    userTime: new Date(creationTime).toISOString(),
  };

  recentSearches.push(zeroResult);
  recentSearches.sort((x, y) => x.creationTime - y.creationTime);

  AuthUtils.updateUser({ history: currentUser.history });
}

/**
 * @ngdoc method
 * @name restoreHistory
 * @description retrieves actual hitset name and hitset key for saved history item
 * */
export function restoreHistory(item) {
  return HistoryResource.restoreHistory(item)
    .then((result) => {
      // eslint-disable-next-line no-param-reassign
      item.resultName = result.resultName;
      // eslint-disable-next-line no-param-reassign
      item.hitSetKey = result.hitSetKey;
      // eslint-disable-next-line no-param-reassign
      item.resultSize = result.resultSize;

      return item;
    })
    .catch(Logger.reject);
}

export function requestSavedHistory() {
  return HistoryResource.getSavedHistory().then(processHistoryItems).catch(Logger.reject);
}

export function processHistoryItems(historyItems) {
  historyItems.forEach((item) => {
    if (item.queryFactItem) {
      // eslint-disable-next-line no-param-reassign
      item.structures = HistoryHelper.extractStructuresFromFacts(item.queryFactItem);
    }
  });

  return splitHistoryByDBs(historyItems);
}

/**
 * @ngdoc method
 * @name formatExistingHistory
 * @description format object before saving to save in BE
 * */
export function formatExistingHistory(historyItem, newSearchName) {
  // eslint-disable-next-line no-param-reassign
  historyItem.searchName = newSearchName;
  return historyItem;
}

const getQuickSearchRefreshPayload = (historyItem) => {
  const query = historyItem?.quickSearchItem?.content || historyItem?.textQuery || '';
  const structure = historyItem?.quickSearchItem?.structure || historyItem?.structure || '';
  const Typeaheads = historyItem?.quickSearchItem?.Typeaheads;

  const hasTypedQuery = !!(historyItem?.quickSearchItem?.content || '').trim();
  const hasExplicitCompoundTypeahead = Array.isArray(Typeaheads) && Typeaheads.some((typeahead) => typeahead?.Category === 'COMPOUND');
  // Drop the structure only for typed reruns that explicitly recorded it as unused (name-resolved
  // GSFN rows store structureUsed: false). Rows restored in a new session lose that metadata, and
  // blanking their stored structure produces different result buckets and a rerun timeout.
  const structureNotUsed = historyItem?.quickSearchItem?.structureUsed === false && !hasExplicitCompoundTypeahead;
  const requestStructure = hasTypedQuery && structureNotUsed ? '' : structure;

  return {
    query,
    structure,
    requestStructure,
    options: historyItem?.quickSearchItem?.options || DEFAULT_QUICK_SEARCH_OPTIONS,
    Typeaheads,
    structureUsed: !!requestStructure,
  };
};

const bucketMetaMatchesRow = (meta, historyItem) =>
  !!meta && `${meta.databaseId}` === `${historyItem?.databaseId}` && meta.context === historyItem?.context;

const rowTargetResultSize = (historyItem) => historyItem?.resultSize ?? historyItem?.totalResult;

export async function rerunQuickSearchHistoryItem(historyItem) {
  // Backend-restored rows (new session) lose structureUsed/Typeaheads, so a text+structure rerun
  // cannot reconstruct the original query (e.g. "structure OR synonym index" document searches
  // return 0). Their persisted whereClauseImportToken is the exact executed query — import it and
  // re-execute over the AdvancedQuery channel instead.
  const quickSearchItem = historyItem?.quickSearchItem;
  const metadataLost = quickSearchItem && quickSearchItem.structureUsed === undefined;
  const hasTextAndStructure = !!(quickSearchItem?.content || '').trim() && !!quickSearchItem?.structure;
  if (metadataLost && hasTextAndStructure && quickSearchItem?.whereClauseImportToken) {
    try {
      const queryFactItem = await MessageBroker.send(RequestUrls.importWhereClause, {
        whereClauseImportToken: quickSearchItem.whereClauseImportToken,
        databaseId: historyItem?.databaseId,
      });
      if (queryFactItem) {
        // The imported queryFactItem is only an execution vehicle — keep it off the resolved row
        // AND out of SearchStorage, otherwise Recent History formatting (formatHistoryData reads
        // SearchStorage by the new searchId) re-attaches it and the row renders as a Query Builder
        // entry with extracted structures instead of the original name + structure quick search.
        const resolved = await rerunAdvancedSearchHistoryItem(
          { ...historyItem, queryFactItem },
          {
            query: quickSearchItem.content,
            structure: quickSearchItem.structure,
            options: quickSearchItem.options || [],
            searchType: SearchTypes.QUICK_SEARCH,
          }
        );
        if (resolved && Object.prototype.hasOwnProperty.call(resolved, 'queryFactItem')) {
          delete resolved.queryFactItem;
        }
        return resolved;
      }
    } catch (error) {
      Logger.error(error);
      // Fall through to the regular text/structure rerun below.
    }
  }

  const { query, structure, requestStructure, options, Typeaheads, structureUsed } = getQuickSearchRefreshPayload(historyItem);

  if (!query && !structure) {
    const error = new Error('Unable to rerun this recent search.');
    error.component = 'History';
    throw error;
  }

  const searchId = await SearchStorage.setNew({
    query,
    structure,
    options,
    Typeaheads,
    searchType: SearchTypes.QUICK_SEARCH,
    structureUsed,
  });

  if (!searchId) {
    const error = new Error('Unable to rerun this recent search.');
    error.component = 'History';
    throw error;
  }

  const requestId = uuid();

  return awaitRerunSearchResult(historyItem, searchId, (reject) => {
    if (!_searchByQuery) {
      const error = new Error('Search not registered. Call History.registerSearchByQuery first.');
      error.component = 'History';
      reject(error);
      return;
    }
    _searchByQuery(query, Typeaheads, requestStructure, searchId, requestId, options);
  });
}

/**
 * Re-executes a Query Builder search from its stored queryFactItem over the AdvancedQuery
 * WebSocket channel. Backend `restore/results` cannot restore recent rows from a previous
 * session, so reruns re-execute the query the same way Quick Search reruns do.
 */
export async function rerunAdvancedSearchHistoryItem(historyItem, searchStorageEntry) {
  const queryFactItem = historyItem?.queryFactItem;
  const databases =
    historyItem?.databases || historyItem?.allDatabases || historyItem?.quickSearchItem?.databases || UserDatabases.getListWithIds();

  if (!queryFactItem) {
    const error = new Error('Unable to rerun this recent search.');
    error.component = 'History';
    throw error;
  }

  // `searchStorageEntry` lets callers keep a different presentation in SearchStorage (e.g. a
  // quick-search rerun executed through an imported queryFactItem must still format as a quick
  // search in Recent History) while the queryFactItem is only sent over the WS channel.
  const searchId = await SearchStorage.setNew(
    searchStorageEntry || {
      queryFactItem,
      databases,
      searchType: SearchTypes.ADVANCED_SEARCH,
    }
  );

  if (!searchId) {
    const error = new Error('Unable to rerun this recent search.');
    error.component = 'History';
    throw error;
  }

  return awaitRerunSearchResult(historyItem, searchId, () => {
    Breadcrumb.resetBreadcrumbs();
    RxSocket.send(RxSocketUrls.AdvancedQuery, {
      queryFactItem,
      context: historyItem?.context,
      databases,
      searchId,
      requestId: uuid(),
    });
  });
}

function awaitRerunSearchResult(historyItem, searchId, startSearch) {
  return new Promise((resolve, reject) => {
    let unsubscribeResults = () => {};
    let unsubscribeErrors = () => {};
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      unsubscribeResults();
      unsubscribeErrors();
    };

    const bucketMetaByQueryId = new Map();
    const bufferedResults = [];
    let fallbackMatch = null;
    const targetResultSize = rowTargetResultSize(historyItem);

    const timeoutId = setTimeout(() => {
      if (settled) return;
      if (fallbackMatch) {
        // eslint-disable-next-line no-use-before-define
        resolveRerun(fallbackMatch.meta, fallbackMatch.payload);
        return;
      }
      cleanup();
      const error = new Error('Timed out while rerunning this recent search.');
      error.component = 'History';
      reject(error);
    }, HISTORY_SEARCH_REFRESH_TIMEOUT_MS);

    const resolveRerun = (bucket, resultPayload) => {
      cleanup();
      const semanticSearchIncluded = bucket?.semanticSearchIncluded ?? resultPayload.semanticSearchIncluded ?? false;
      const semanticResultName = semanticSearchIncluded ? (bucket?.semanticResultName ?? resultPayload.semanticResultName) : undefined;
      const textQuery = semanticSearchIncluded ? (resultPayload.textQuery ?? historyItem?.textQuery) : undefined;
      resolve({
        ...historyItem,
        searchId: resultPayload.searchId,
        resultName: resultPayload.resultName,
        hitSetKey: resultPayload.hitSetKey,
        resultSize: resultPayload.resultSize ?? historyItem?.resultSize,
        totalResult: resultPayload.resultSize ?? historyItem?.totalResult,
        recordCount: resultPayload.recordCount ?? historyItem?.recordCount,
        semanticSearchIncluded,
        semanticResultName,
        textQuery,
      });
    };

    const tryMatchPerBucketPayload = (resultPayload) => {
      const meta = bucketMetaByQueryId.get(String(resultPayload.queryId));
      if (!bucketMetaMatchesRow(meta, historyItem)) return false;
      const resultSizeMatches =
        targetResultSize == null || (resultPayload.resultSize != null && `${resultPayload.resultSize}` === `${targetResultSize}`);
      if (resultSizeMatches) {
        resolveRerun(meta, resultPayload);
        return true;
      }
      if (!fallbackMatch) {
        fallbackMatch = { meta, payload: resultPayload };
      }
      return false;
    };

    unsubscribeResults = SocketBroker.observe(RxSocketUrls.ChannelsUrls.SearchInfo, (data) => {
      if (settled) return;
      const payload = data?.payload || data?.data?.payload;
      if (!payload || payload.searchId !== searchId) return;

      if (Array.isArray(payload.items)) {
        payload.items.forEach((item) => {
          if (item?.queryId != null) {
            bucketMetaByQueryId.set(String(item.queryId), item);
          }
        });
        // eslint-disable-next-line no-restricted-syntax
        for (const buffered of bufferedResults) {
          if (tryMatchPerBucketPayload(buffered)) return;
        }
        return;
      }

      if (!payload.resultName) return;
      if (!bucketMetaByQueryId.size) {
        bufferedResults.push(payload);
        return;
      }
      tryMatchPerBucketPayload(payload);
    });

    unsubscribeErrors = SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, (data) => {
      if (settled) return;
      const payload = data?.payload || data?.data?.payload;
      if (payload?.searchId && payload.searchId !== searchId) {
        return;
      }

      cleanup();
      if (data?.exception) {
        reject(data.exception);
      } else {
        const error = new Error('Failed to rerun this recent search.');
        error.component = 'History';
        reject(error);
      }
    });

    startSearch(reject);
  });
}

function resolveActiveItem(item) {
  if (item) return item;
  const databaseId = AuthUtils.getUser()?.history?.viewedResultsParams?.databaseId;
  const breadcrumbs = Breadcrumb.retrieveBreadcrumbs(databaseId);
  return breadcrumbs.find((crumb) => crumb.active === true);
}

function hasFallbackParams(prevStateParams) {
  return !!(prevStateParams?.databaseId && prevStateParams?.context && prevStateParams?.resultName && prevStateParams?.searchId);
}

function buildBaseParams(val) {
  return {
    databaseId: val('databaseId'),
    context: val('context'),
    resultName: val('resultName'),
    searchId: val('searchId'),
    hitSetKey: val('hitSetKey'),
    fromContext: val('fromContext'),
    limitOrExclude: val('limitOrExclude'),
  };
}

function navigateToHeatmap(stateParams, val) {
  assignIn(stateParams, {
    context: 'dpitems',
    resultName: val('heatmapResultName'),
    index: val('index'),
    isRow: val('isRow'),
    isHeatmap: true,
    ascending: val('ascending'),
    sortedMatrixName: val('sortedMatrixName'),
  });
  history.push({ pathname: generateHeatmapPath(stateParams), state: stateParams });
}

function navigateToResults(stateParams, val) {
  assignIn(stateParams, { pageNumber: 1, order: val('order'), sort: val('sort') });
  history.push({ pathname: generateResultsPath(stateParams), state: stateParams });
}

export function goToResultPage(item) {
  const prevStateParams = getCombinedRouteParams(store.getState());
  // RXFE-4881: breadcrumb may not yet exist for large result sets
  const resolvedItem = resolveActiveItem(item);

  if (!resolvedItem && !hasFallbackParams(prevStateParams)) return;

  // val() picks from the resolved item when available, otherwise falls back to prevStateParams
  const val = (key) => (resolvedItem ? resolvedItem[key] : prevStateParams[key]);
  const stateParams = buildBaseParams(val);

  if (resolvedItem?.isHeatmapShown) {
    navigateToHeatmap(stateParams, val);
  } else {
    navigateToResults(stateParams, val);
  }
}

export function showStructurePopup(structure) {
  store.dispatch(
    openStructureDialog(uuid(), structure, {
      name: parseV3000SumFormula(structure),
      isLinearFormula: true,
    })
  );
}

export function openConfirmPopup(content, opts = {}) {
  return new Promise((resolve, reject) => {
    store.dispatch(
      updateDialog({
        id: typeOfHistoryConfirmationDialog,
        dialogType: typeOfHistoryConfirmationDialog,
        backdrop: false,
        ...content,
        ...opts,
        onClose: () => {
          reject();
          store.dispatch(closeDialog({ id: typeOfHistoryConfirmationDialog }));
        },
        onConfirm: () => {
          resolve();
          store.dispatch(closeDialog({ id: typeOfHistoryConfirmationDialog }));
        },
      })
    );
  });
}

/* ----------------------- RECENT HISTORY --------------------------- */
/**
 * @description merge in-memory recent searches with session-only entries (filter/sort/order
 * updates that never hit the recent-history POST API). Exact duplicate records are overwritten by
 * the session copy; distinct timestamps remain distinct rows.
 * */
function mergeRecentSearchesWithSession(userRecentSearches) {
  const sessionEntries = HistoryHelper.getSessionOnlyRecentHistory();
  if (!sessionEntries.length) return userRecentSearches;

  return HistoryHelper.mergeRecentHistoryEntries(userRecentSearches, sessionEntries, 'asc');
}

/**
 * @ngdoc method
 * @name retrieveRecentHistory
 * @description retrieve the recent searches (including 0 results) that is in the appStorage for the current user,
 * merged with any session-only entries (filter/sort/order updates).
 * @returns {Array} all recent searches of user
 * */
export function retrieveRecentHistory() {
  return mergeRecentSearchesWithSession(get(AuthUtils.getUser(), 'history.recentSearches', []));
}

/**
 * @ngdoc method
 * @name retrieveRecentHistoryWithoutZeroResults
 * @description retrieve the recent searches (without 0 results) merged with session-only entries.
 * @returns {Array} all recent searches of user without 0 results
 * */
export function retrieveRecentHistoryWithoutZeroResults() {
  return mergeRecentSearchesWithSession(get(AuthUtils.getUser(), 'history.recentSearches', [])).filter((search) => search.resultSize !== 0);
}

/**
 * @description store breadcrumbs array in recentSearches so that they don't
 * get lost when the breadcrumbs are reset
 * */
export function storeRecentHistory(sort, order) {
  const activeDatabaseId = AuthUtils.getUser().history.viewedResultsParams.databaseId;
  const activeBreadcrumb = Breadcrumb.getActiveBreadcrumbForDb(activeDatabaseId);
  if (!activeBreadcrumb?.resultName) {
    return;
  }
  const historyItem = {
    ...activeBreadcrumb,
    ...(sort !== undefined && { sort }),
    ...(order !== undefined && { order }),
  };
  HistoryHelper.storeRecentHistory(historyItem);
}

/* ----------------------- SAVED HISTORY --------------------------- */

export function openSaveHistoryModal(historyItem) {
  if (AuthUtils.isAnonymousUser()) {
    const errorTitle = 'Login required';
    const errorMessage = 'Please sign-in/register to save your History.';
    return NotifyDialogsService.openModalWindow({
      title: errorTitle,
      text: errorMessage,
      cancelText: 'Cancel',
      confirmText: 'Sign in',
      type: 'info',
      backdrop: 'static',
      onConfirm: () => {
        Catalyst.trackConversionDriverClick('history:savesearch:sign-in');
        AppStorage.instance.set('scheduledHistory', historyItem);

        AppStorage.instance.setToSessionStorage(
          HISTORY_LOGIN_REDIRECT_PATH_STORAGE_KEY,
          history.location?.pathname?.includes('saved') ? paths.HISTORY_SAVED : paths.HISTORY_RECENT
        );
        history.push({
          pathname: generatePath(paths.LOGIN),
        });
      },
      onCancel: () => {
        AppStorage.instance.set('scheduledHistory', null);
      },
      trackingOptions: {
        message: `${errorTitle} ${errorMessage}`,
        component: 'History',
      },
    }).result;
  }
  const promise = new Promise(() => {
    store.dispatch(updateDialog({ id: typeOfHistorySaveDialog, dialogType: typeOfHistorySaveDialog }));
    store.dispatch({
      type: HANDLE_VIEW_SAVE_MODAL,
      show: true,
      itemToBeSavedContext: historyItem.context || '',
      title: 'Save',
      historyItem,
      itemIndex: 0,
      groupItemIndex: 0,
    });
    AppStorage.instance.set('scheduledHistory', null);
  });
  return promise;
}

export function getSavedHistory() {
  return HistoryResource.getSavedHistory();
}

export function removeHistory(historyItem) {
  return HistoryResource.removeHistory(historyItem);
}

/**
 * @ngdoc function
 * @name splitHistoryByDBs
 * @param {array} historyItems List of history items objects
 * @description Restructures history items array into the other array of databases,
 *              where each item contains databaseName it's own list of history items.
 *              Sorts databases in alphabetical order, keeping Reaxys DB on first position.
 */
export function splitHistoryByDBs(historyItems) {
  return flow([
    groupBy('databaseId'),
    map((items, key) => {
      const name = UserDatabases.getDbName(key);
      return name
        ? {
            databaseId: key,
            priority: UserDatabases.isDefaultDatabase(key) ? '' : name.toLowerCase(),
            databaseName: name,
            items,
          }
        : null;
    }),
    toArray,
    compact,
    sortBy('priority'),
  ])(historyItems);
}

export function updateItemsComments(databaseStorage) {
  each(databaseStorage, (database) => {
    // eslint-disable-next-line no-param-reassign,no-return-assign
    each(database.items, (item) => (item.comment = sanitizeEscape(item.comment, { allowedTags: ['sub', 'sup', 'i', 'hi'] })));
  });
}
